import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "../../utils/analytics";
import { applyStructuredData } from "../../utils/structuredData";

const SITE = "https://www.nexorahai.com";
const OG_IMAGE = `${SITE}/assets/nexorah-logo.png`;
const API_BASE =
  process.env.REACT_APP_BASE_URL || "http://localhost:8000/api";

const DEFAULT_DESCRIPTION =
  "Prepare for the CPHQ exam with structured modules, exam-style practice, mock exams, and AI-guided study support for healthcare quality professionals.";

const LEARNING_HUB_META = {
  title: "CPHQ Articles & Study Guides | Nexorah Learning Hub",
  description:
    "Free CPHQ certification articles and study guides on exam prep, patient safety, quality improvement, data analytics, and all seven healthcare quality domains.",
};

/** Indexable marketing & legal pages (core URLs also in sitemap.xml). */
const PUBLIC_PAGES = {
  "/": {
    title: "Nexorah | CPHQ Exam Preparation Online",
    description:
      "Online CPHQ exam preparation with video modules, practice questions, full-length mock exams, study planner, and AI tutor support for healthcare quality professionals.",
  },
  "/learning-hub": LEARNING_HUB_META,
  "/sign-in": {
    title: "Sign In | Nexorah CPHQ Exam Prep",
    description:
      "Sign in to Nexorah to continue your CPHQ exam preparation—access course videos, question bank, mock exams, and your study dashboard.",
  },
  "/sign-up": {
    title: "Create Account | Nexorah CPHQ Exam Prep",
    description:
      "Create your Nexorah account to start CPHQ certification prep with guided modules, exam-style quizzes, progress tracking, and subscription plans.",
  },
  "/contact": {
    title: "Contact Us | Nexorah",
    description:
      "Contact Nexorah for CPHQ course help, subscription and billing questions, technical support, or partnership inquiries.",
  },
  "/terms": {
    title: "Terms of Use | Nexorah",
    description:
      "Read the Terms of Use for Nexorah, the online CPHQ exam preparation platform for healthcare quality professionals.",
  },
  "/privacy": {
    title: "Privacy Policy | Nexorah",
    description:
      "Learn how Nexorah collects, uses, stores, and protects your personal data when you use our CPHQ exam preparation services.",
  },
};

/** Public auth flows — unique meta for tabs & sharing, but not indexed. */
const NOINDEX_PUBLIC_PAGES = {
  "/verify-email": {
    title: "Verify Email | Nexorah",
    description:
      "Verify your email address to activate your Nexorah account and access CPHQ exam preparation content.",
  },
  "/forgot-password": {
    title: "Forgot Password | Nexorah",
    description:
      "Reset access to your Nexorah account. Enter your email to receive a secure password reset link.",
  },
  "/reset-password": {
    title: "Reset Password | Nexorah",
    description: "Choose a new password for your Nexorah account to sign in and continue CPHQ exam preparation.",
  },
};

const PRIVATE_PAGES = {
  "/dashboard": {
    title: "Dashboard | Nexorah",
    description: "Your Nexorah dashboard—CPHQ readiness, study progress, and recommended next steps.",
  },
  "/resources": {
    title: "Course Resources | Nexorah",
    description: "CPHQ course modules, chapter videos, and chapter quizzes on Nexorah.",
  },
  "/study-planner": {
    title: "Study Planner | Nexorah",
    description: "Plan your CPHQ study schedule with Nexorah's personalized study planner.",
  },
  "/question-bank": {
    title: "Question Bank | Nexorah",
    description: "CPHQ practice questions, mock exams, and final exams on Nexorah.",
  },
  "/study-vault": {
    title: "Study Vault | Nexorah",
    description: "Flashcards, summaries, and reference materials for CPHQ exam prep.",
  },
  "/account-settings": {
    title: "Account Settings | Nexorah",
    description: "Manage your Nexorah account profile, subscription, and preferences.",
  },
  "/change-password": {
    title: "Change Password | Nexorah",
    description: "Update the password for your Nexorah account.",
  },
  "/onboarding": {
    title: "Welcome | Nexorah",
    description: "Complete onboarding to personalize your CPHQ exam preparation on Nexorah.",
  },
  "/user-profile": {
    title: "Profile | Nexorah",
    description: "View and edit your Nexorah learner profile.",
  },
  "/payment/success": {
    title: "Payment Successful | Nexorah",
    description: "Your Nexorah subscription payment was successful.",
  },
  "/payment/cancel": {
    title: "Payment Cancelled | Nexorah",
    description: "Your Nexorah checkout was cancelled. You can try again anytime.",
  },
  "/quick-quiz": {
    title: "Quick Quiz | Nexorah",
    description: "Chapter quiz for CPHQ course modules on Nexorah.",
  },
};

function setMeta(attribute, key, content) {
  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function applyDocumentMeta(meta, pathname) {
  const {
    title,
    description,
    indexable,
    canonicalPath,
    keywords,
    image,
    type,
    twitterCard,
  } = meta;

  document.title = title;
  setMeta("name", "description", description);
  setMeta("name", "robots", indexable ? "index, follow" : "noindex, nofollow");
  if (keywords) setMeta("name", "keywords", keywords);
  else document.head.querySelector('meta[name="keywords"]')?.remove();

  setMeta("property", "og:type", type || "website");
  setMeta("property", "og:site_name", "Nexorah");
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", description);
  setMeta("property", "og:url", `${SITE}${pathname}`);
  setMeta("property", "og:image", image || OG_IMAGE);

  setMeta("name", "twitter:card", twitterCard || "summary");
  setMeta("name", "twitter:title", title);
  setMeta("name", "twitter:description", description);
  setMeta("name", "twitter:image", image || OG_IMAGE);

  const canonical = canonicalPath === "/" ? `${SITE}/` : `${SITE}${canonicalPath}`;
  setCanonical(canonical);
}

function resolveStaticPageMeta(path, search) {
  if (path === "/pdf-viewer") {
    const name = new URLSearchParams(search).get("name");
    return {
      title: name ? `${name} | Nexorah Study Vault` : "Study Vault | Nexorah",
      description: "CPHQ study material in Nexorah Study Vault.",
      indexable: false,
      canonicalPath: "/",
    };
  }

  const publicPage = PUBLIC_PAGES[path];
  if (publicPage) {
    return {
      ...publicPage,
      indexable: true,
      canonicalPath: path === "/" ? "/" : path,
    };
  }

  const noindexPublic = NOINDEX_PUBLIC_PAGES[path];
  if (noindexPublic) {
    return {
      ...noindexPublic,
      indexable: false,
      canonicalPath: "/",
    };
  }

  const privatePage = PRIVATE_PAGES[path];
  if (privatePage) {
    return {
      ...privatePage,
      indexable: false,
      canonicalPath: "/",
    };
  }

  return {
    title: "Nexorah | CPHQ Exam Preparation",
    description: DEFAULT_DESCRIPTION,
    indexable: false,
    canonicalPath: "/",
  };
}

async function fetchArticleMeta(slug) {
  const response = await fetch(`${API_BASE}/learning-hub/articles/${slug}/`);
  if (!response.ok) {
    throw new Error("Article not found");
  }
  return response.json();
}

function metaFromArticle(article, pathname) {
  const titleText = article.seo_title_resolved || article.title || "Article";
  const description =
    article.seo_description_resolved || article.summary || DEFAULT_DESCRIPTION;
  const image = article.og_image_url || article.cover_image_url || OG_IMAGE;

  return {
    title: `${titleText} | Nexorah`,
    description,
    indexable: true,
    canonicalPath: pathname,
    image,
    type: "article",
    twitterCard: image !== OG_IMAGE ? "summary_large_image" : "summary",
    article,
  };
}

export default function TitleUpdater() {
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const pathname = location.pathname;

    const run = async () => {
      const articleMatch = pathname.match(/^\/learning-hub\/([^/]+)$/);

      if (articleMatch) {
        try {
          const article = await fetchArticleMeta(articleMatch[1]);
          if (cancelled) return;
          const meta = metaFromArticle(article, pathname);
          applyDocumentMeta(meta, pathname);
          applyStructuredData(pathname, meta.description, article);
          trackPageView(`${pathname}${location.search || ""}`);
          return;
        } catch {
          if (cancelled) return;
          applyDocumentMeta(
            {
              title: "Article not found | Nexorah",
              description: DEFAULT_DESCRIPTION,
              indexable: false,
              canonicalPath: "/learning-hub",
            },
            pathname
          );
          applyStructuredData(pathname, DEFAULT_DESCRIPTION, null);
          trackPageView(`${pathname}${location.search || ""}`);
          return;
        }
      }

      const meta = resolveStaticPageMeta(pathname, location.search);
      if (cancelled) return;
      applyDocumentMeta(meta, pathname);
      applyStructuredData(pathname, meta.description, null);
      trackPageView(`${pathname}${location.search || ""}`);
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search]);

  return null;
}
